import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import _ from 'lodash';

export interface S3Folder {
  name: string;
  path: string;
  totalCount: number;
  createdAt: string;
}

export type ImageItem = {
  src: string;
  name: string;
  sku: string;
  price: number;
  linkUrl: string;
  id: string;
};

export interface S3File {
  name: string;
  path: string;
  key: string;
  size: number;
  lastModified: string | null;
  productId: string;
  productName: string;
  sku: string;
  price: number;
  sourceLink: string;
}
export function toImagesFormat(
  files: S3File[],
  opts?: {
    defaultName?: string;
    defaultSku?: string;
    defaultPrice?: number;
    defaultLinkUrl?: string;
  }
): ImageItem[] {
  const toTitle = (base: string) =>
    base
      .replace(/\.[^/.]+$/g, '') // strip extension
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  return files
    ?.filter((f) => f.productName && !f.productName.startsWith('.'))
    .map((f, i) => {
      const autoName = toTitle(f.productName);
      return {
        src: f.path,
        name: autoName ?? opts?.defaultName,
        sku: f?.sku ?? opts?.defaultSku,
        price: f?.price ?? opts?.defaultPrice,
        linkUrl: f?.sourceLink ?? '',
        id: f?.productId ?? '',
      };
    });
}

export function toImagesObject(
  folderName: string,
  files: S3File[],
  opts?: Parameters<typeof toImagesFormat>[1]
) {
  return { [folderName]: toImagesFormat(files, opts) };
}

const BASE_URL_QA = `https://umk7dugplzeyuarkdzeefifvja0gwblu.lambda-url.ap-south-1.on.aws`;

const BLOB_BASE_URL_QA = `https://veidemdwrtcvrrmqx3a32jwi440glfso.lambda-url.ap-south-1.on.aws`;

const BASE_URL_PROD = `https://2ym7iirhraezwjg3u3jplm33im0yaexh.lambda-url.ap-south-1.on.aws`;

const BLOB_BASE_URL_PROD = `https://4dhieovjs2cd2gtasl5s3pajlq0nuqaj.lambda-url.ap-south-1.on.aws`;

async function listGallery(workspaceId: string, isQa: boolean) {
  const baseUrl = isQa ? BASE_URL_QA : BASE_URL_PROD;
  const res = await fetch(`${baseUrl}/gallery/${workspaceId}`);
  if (!res.ok) throw new Error(`Failed to fetch gallery: ${res.statusText}`);
  return res.json() as Promise<{
    workspaceId: string;
    basePrefix: string;
    folders: S3Folder[];
    files: S3File[];
  }>;
}

async function getSignedImageUrl(
  workspaceId: string,
  objectPath: string,
  isQa: boolean
) {
  const baseUrl = isQa ? BASE_URL_QA : BASE_URL_PROD;
  const res = await fetch(
    `${baseUrl}/gallery/${workspaceId}/${encodeURIComponent(objectPath)}`
  );
  if (!res.ok) throw new Error(`Failed to get image URL: ${res.statusText}`);
  const data = await res.json();
  return data.url as string;
}

async function listFolderFiles(
  workspaceId: string,
  folderPath: string,
  isQa: boolean
) {
  const baseUrl = isQa ? BASE_URL_QA : BASE_URL_PROD;
  const res = await fetch(
    `${baseUrl}/gallery/${workspaceId}/${encodeURIComponent(folderPath)}`
  );
  if (!res.ok)
    throw new Error(`Failed to fetch folder files: ${res.statusText}`);
  const data = await res.json();
  return data.files as S3File[];
}

export async function getImageBlobFromLambda(
  s3Url: string,
  isQa: boolean
): Promise<Blob> {
  const baseUrl = isQa ? BLOB_BASE_URL_QA : BLOB_BASE_URL_PROD;

  // We send the S3 key, not the full URL
  const url = `${baseUrl}/blob/${encodeURIComponent(s3Url)}`;

  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(
      `Failed to fetch image blob: ${res.status} ${res.statusText}`
    );
  }

  const contentType = res.headers.get('Content-Type') || 'image/png';
  const arrayBuffer = await res.arrayBuffer();
  return new Blob([arrayBuffer], { type: contentType });
}

// ✅ Hook: fetches folders + files from Lambda
export const useFolderList = () => {
  const [folders, setFolders] = useState<S3Folder[]>([]);
  const [rootFiles, setRootFiles] = useState<S3File[]>([]);
  const [files, setFiles] = useState<S3File[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const params = useParams();
  const [searchParams] = useSearchParams(); // <-- add support for query string
  const location = useLocation();
  const domainName =
    typeof window !== 'undefined'
      ? window.location.hostname
      : (location?.pathname ?? '');

  const isQa = domainName.includes('-qa') || !domainName.includes('mobile');

  // Example: convert query params to an object
  const query = Object.fromEntries(searchParams.entries());
  const workspaceId = query.id;
  useEffect(() => {
    if (!workspaceId) return;

    if (_.isEmpty(folders)) {
      setLoading(true);
      listGallery(workspaceId, isQa)
        .then((data) => {
          setFolders(data.folders);
          setRootFiles(data.files);
          setError(null);
        })
        .catch((err) => {
          setError(err.message || 'Failed to load gallery');
        })
        .finally(() => setLoading(false));
    }
  }, [workspaceId]);

  const foldersData = useMemo(() => {
    return folders.map((folder) => ({
      id: folder.name,
      name: folder.name,
      date: moment.utc(folder.createdAt).local().format('DD/MM/YYYY'),
      items: folder.totalCount,
    }));
  }, [folders]);

  useEffect(() => {
    if (params?.folderId) {
      const folder = folders.find((f) => f.name === params?.folderId);
      console.log('folder', folders, params?.folderId, folder);
      if (!folder) return;
      setLoading(true);
      listFolderFiles(workspaceId, folder.path, isQa)
        .then(setFiles)
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }
  }, [workspaceId, params?.folderId, folders]);

  const images = toImagesObject(params?.folderId ?? '', files, {
    defaultSku: 'SKU771288219',
    defaultPrice: 600,
    defaultLinkUrl:
      'https://www.pexels.com/photo/cool-man-in-t-shirt-posing-in-studio-20605798/',
  });

  return {
    folders: foldersData,
    images,
    files: rootFiles,
    loading,
    error,
    getSignedImageUrl,
    query,
  };
};
